import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import { isString } from "@/utils/text";

export interface AppApiResponse<T> {
  success: boolean;
  data: T;
  status: number;
  message?: string;
}

// Fonction pour déconnecter l'utilisateur
const logoutUser = () => {
  // Nettoyer le localStorage
  localStorage.removeItem("auth-storage");
  localStorage.removeItem("campagne-store");
  localStorage.removeItem("commande-store");

  // Rediriger vers la page de connexion
  window.location.href = "/#/connexion";
};

// Fonction pour vérifier si l'utilisateur doit être déconnecté
const shouldLogout = (status: number, errorData: any): boolean => {
  // Vérifier le statut 401
  if (status === 401) {
    return true;
  }

  // Vérifier le message d'erreur spécifique
  if (errorData?.error === "Tentative d'accès sans JSESSIONID") {
    return true;
  }

  return false;
};

// Création d'une instance Axios de base
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api",
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // Pour les cookies et les sessions
});

// Intercepteur pour ajouter le token d'auth si présent
api.interceptors.request.use((config) => {
  const authStore = localStorage.getItem("auth-storage");
  if (authStore) {
    try {
      const parsed = JSON.parse(authStore);
      if (parsed?.state?.token) {
        config.headers = config.headers;
        config.headers["Authorization"] = `Bearer ${parsed.state.token}`;
      }
    } catch {}
  }
  return config;
});

// Utilitaire hook/fonction pour requêtes
const useApi = () => {
  const useGetApi = async <T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AppApiResponse<T>> => {
    try {
      const res = await api.get<T>(url, config);
      return {
        success: true,
        data: res.data,
        status: res.status,
        message: res.statusText,
      };
    } catch (error: any) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data;

      // Vérifier si l'utilisateur doit être déconnecté
      if (shouldLogout(status, errorData)) {
        logoutUser();
      }

      return {
        success: false,
        data: error?.response?.data || (undefined as unknown as T),
        status,
        message:
          errorData?.message || errorData?.error || isString(errorData)
            ? errorData
            : error?.message || "An error occurred",
      };
    }
  };

  const usePostApi = async <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AppApiResponse<T>> => {
    try {
      const res = await api.post<T>(url, data, config);
      return {
        success: true,
        data: res.data,
        status: res.status,
        message: res.statusText,
      };
    } catch (error: any) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data;

      // Vérifier si l'utilisateur doit être déconnecté
      if (shouldLogout(status, errorData)) {
        logoutUser();
      }

      return {
        success: false,
        data: errorData || (undefined as unknown as T),
        status,
        message:
          errorData?.message || errorData?.error || isString(errorData)
            ? errorData
            : error?.message || "An error occurred",
      };
    }
  };

  const usePutApi = async <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AppApiResponse<T>> => {
    try {
      const res = await api.put<T>(url, data, config);
      return {
        success: true,
        data: res.data,
        status: res.status,
        message: res.statusText,
      };
    } catch (error: any) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data;

      // Vérifier si l'utilisateur doit être déconnecté
      if (shouldLogout(status, errorData)) {
        logoutUser();
      }

      return {
        success: false,
        data: undefined as unknown as T,
        status,
        message:
          errorData?.message || errorData?.error || isString(errorData)
            ? errorData
            : error?.message || "An error occurred",
      };
    }
  };

  const usePatchApi = async <T>(
    url: string,
    data: any,
    config?: AxiosRequestConfig
  ): Promise<AppApiResponse<T>> => {
    try {
      const res = await api.patch<T>(url, data, config);
      return {
        success: true,
        data: res.data,
        status: res.status,
        message: res.statusText,
      };
    } catch (error: any) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data;

      // Vérifier si l'utilisateur doit être déconnecté
      if (shouldLogout(status, errorData)) {
        logoutUser();
      }

      return {
        success: false,
        data: undefined as unknown as T,
        status,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "An error occurred",
      };
    }
  };

  const useDeleteApi = async <T>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<AppApiResponse<T>> => {
    try {
      const res = await api.delete<T>(url, config);
      return {
        success: true,
        data: res.data,
        status: res.status,
        message: res.statusText,
      };
    } catch (error: any) {
      const status = error?.response?.status || 500;
      const errorData = error?.response?.data;

      // Vérifier si l'utilisateur doit être déconnecté
      if (shouldLogout(status, errorData)) {
        logoutUser();
      }

      return {
        success: false,
        data: undefined as unknown as T,
        status,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "An error occurred",
      };
    }
  };

  return {
    useGetApi,
    usePostApi,
    usePutApi,
    usePatchApi,
    useDeleteApi,
  };
};

export default useApi;
